import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'

export interface ModuleScore {
  moduleId: string
  title: string
  crew: string
  teams: string[]
  phase: string
  path: string
  attempts: number
  totalScore: number
  totalMaxScore: number
  averageScore: number
  passed: boolean
  latestDate: string | null
}

export interface ScoreStats {
  totalModulesAttempted: number
  totalAttempts: number
  overallAverage: number
  completedCount: number
  completionRate: number
}

export interface ScoresData {
  stats: ScoreStats
  modules: ModuleScore[]
}

export function useScores(filter?: { crew?: string; team?: string }) {
  const [data, setData] = useState<ScoresData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await api.getKnowledgeCheckScores(filter)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scores')
    } finally {
      setLoading(false)
    }
  }, [filter?.crew, filter?.team])

  useEffect(() => {
    let cancelled = false
    async function fetchData() {
      setLoading(true)
      setError(null)
      try {
        const result = await api.getKnowledgeCheckScores(filter)
        if (!cancelled) {
          setData(result)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load scores')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, [filter?.crew, filter?.team])

  return { data, loading, error, refetch: load }
}